import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import classNames from "classnames";

import { StatusCode } from "api/protocols";
import {
  DeleteFacilityRole,
  GetFacilityAdmin,
  GetFacilityRoles,
  IFacilityRole,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

import { showError, showSuccess } from "redux/actions/ui";
import { IFacilityStaff } from "redux/reducers/models/facility";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import { Badge } from "components/badge/Badge";
import { faEllipsisV } from "@fortawesome/pro-regular-svg-icons";
import DropFilter from "components/dropdown/DropFilter";
import DataTable from "../../../../../components/dataTable/DataTable";
import "./facility.scss";
import Tabs from "components/tabs/Tabs";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonNew } from "components/buttonNew";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { capitalize } from "helpers/Helpers";
import StaffAccountShiftTable from "../shifts/staffAccountShiftTable/StaffAccountShiftTable";
import Input from "components/form/input/Input";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

type FacilityStaffFilters = {
  accountState: Set<"active" | "disabled">;
  roleState: Set<number>;
};

enum tabSelection {
  STAFF = 0,
  ROLES = 1,
  SHIFTS = 2,
}

interface IFilterState {
  offset: number;
  search: string;
}

const TABLE_LIMIT = 50;
export default function FacilityStaff() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store);
  const tabs = [
    {
      id: "staff",
      content: t("Staff Members"),
    },
    {
      id: "roles",
      content: t("Roles & Permissions"),
    },
    ...(authStore?.user?.permissions?.admin_edit_shift_history ? [{
      id: "shift-summary",
      content: t("Shift Summary"),
    }] : []),
  ];
  const [selected, setSelected] = useState<tabSelection>(tabSelection.STAFF);

  const [filterState, setFilterState] = useState<IFilterState>({
    offset: 0,
    search: "",
  });

  const [staffFilters, setStaffFilters] = useState<FacilityStaffFilters>({
    accountState: new Set(["active"]),
    roleState: new Set<number>(),
  });

  const [roleState, setRoleState] = useState<IFacilityRole[]>(null);
  const [adminData, setAdminData] = useState<Array<IFacilityStaff & {roles?: Array<{id: number; title: string; wage?: string}>}>>(undefined);
  const [shiftStaffMembers, setShiftStaffMembers] = useState<IFacilityStaff[]>(undefined);

  const { state: deleteRolePopup, updateModal: updateDeleteRolePopup } = useModal({
    selectedID: 0,
  });

  /* adminData filtered to display specific accounts based off staffFilters.accountState */
  const filteredAdmins = useMemo(() => {
    if (!adminData) {
      return undefined;
    }

    const { accountState, roleState } = staffFilters;

    // No filters at all → return everything
    if (accountState.size === 0 && roleState.size === 0) {
      return adminData;
    }

    return adminData.filter(admin => {
      // ----- account state filter -----
      const normalizedState = (admin.state ?? "active") as "active" | "disabled";
      const stateMatch = accountState.size === 0 || accountState.has(normalizedState);

      // ----- role filter -----
      const roleMatch = roleState.size === 0 || admin?.roles?.some(role => roleState?.has(role?.id));

      return stateMatch && roleMatch;
    });
  }, [adminData, staffFilters.accountState, staffFilters.roleState]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFacilityStaff(source.token);
    void loadFacilityRoles(source.token);
    if (authStore?.user?.permissions?.admin_edit_shift_history) {
      void loadShiftStaffMembers(source.token);
    }

    const lastSelectedTab = sessionStorage.getItem("last_selected_staff_tab");
    if (lastSelectedTab !== undefined) {
      setSelected(Number(lastSelectedTab));
    }
    return () => source.cancel();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("last_selected_staff_tab", String(selected));
  }, [selected]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFacilityStaff(source.token);
    return () => source.cancel();
  }, [filterState]);

  async function loadFacilityStaff(token?: CancelToken) {
    if (adminData !== undefined) {
      setAdminData(undefined);
    }

    const res = await GetFacilityAdmin(
      {
        limit: TABLE_LIMIT,
        roles: true,
        ...filterState,
      },
      token ? false : true,
      token,
    );
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts for the facility.")));
    }

    setAdminData(res.status !== StatusCode.OK ? [] : res.data);
  }

  async function loadShiftStaffMembers(token?: CancelToken) {
    const res = await GetFacilityAdmin({roles: true}, token ? false : true, token);

    if (token?.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts for the facility.")));
      setShiftStaffMembers([]);
      return;
    }

    setShiftStaffMembers(res.data);
  }

  async function loadFacilityRoles(token?: CancelToken) {
    const roleRes = await GetFacilityRoles({ admins: true }, true, token);

    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility roles")));
    }

    setRoleState(roleRes.data);
  }

  async function deleteRole() {
    const deleteRoleRes = await DeleteFacilityRole({ id: deleteRolePopup.selectedID }, true);
    if (deleteRoleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting role from facility")));
      return;
    }

    dispatch(showSuccess(t("Role successfully deleted")));
    updateDeleteRolePopup({ isOpen: false, selectedID: 0 });
    void loadFacilityRoles();
  }

  function handleApplyFilters(filterReturn: Record<string, any>[], id: string) {
    const filterIds: (string | number)[] = filterReturn.map(filter => filter.id as string | number);
    setStaffFilters({
      ...staffFilters,
      [id]: new Set([...filterIds]),
    });
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  function sendToNewRole() {
    history.push("/admin/settings/roles_new");
  }

  const getAdminCountForRole = (roleId: number): number => {
    return adminData?.filter(admin => admin?.state !== "disabled" && admin?.roles?.some(role => role?.id === roleId))?.length ?? 0;
  };

  return (
    <Page
      title={t("Staff Accounts")}
      subtitle={
        selected === tabSelection.STAFF
          ? t("Manage who has access to your facilities")
          : selected === tabSelection.ROLES
            ? t("Set up access permissions and manage staff assignments for this role.")
            : selected === tabSelection.SHIFTS ? t("View and edit staff shifts")
              : null
      }
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
      ]}
    >
      <Page.Section>
        <div style={{ display: "flex", overflowX: "auto", paddingBottom: 24 }}>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fullWidth variant="wide" />
        </div>
      </Page.Section>

      {selected === tabSelection.STAFF ? (
        <>
          <div className="pb-2">
            <Input
              placeholder={t("Search by name, email, or username")}
              onChange={e => setFilterState(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <Card
            title={
              <div className="flex items-center gap-2">
                {t("Staff Accounts")}{" "}
                <Badge type={"public"} size="medium">
                  {filteredAdmins?.length}
                </Badge>
              </div>
            }
            titleActions={[
              {
                content: (
                  <DropFilter
                    filterType="Checkbox"
                    title={t("Account State")}
                    filterData={[
                      { id: "active", label: t("Active") },
                      { id: "disabled", label: t("Disabled") },
                    ]}
                    filterIdPropFromData="id"
                    filterLabelPropFromData="label"
                    applyFilters={filterReturn => handleApplyFilters(filterReturn, "accountState")}
                    defaultCheckboxes={[...staffFilters.accountState]}
                    leftAligned
                  />
                ),
                action: () => console.log(),
              },
              {
                content: (
                  <DropFilter
                    filterType="Checkbox"
                    title={t("Roles")}
                    filterData={roleState}
                    filterIdPropFromData="id"
                    filterLabelPropFromData="title"
                    applyFilters={filterReturn => handleApplyFilters(filterReturn, "roleState")}
                    defaultCheckboxes={[...staffFilters.roleState]}
                    leftAligned
                  />
                ),
                action: () => console.log(),
              },
              {
                content: t("+ New Staff Account"),
                action: () => history.push("/admin/settings/staff_new"),
                buttonType: "primary",
              },
            ]}
          >
            <div className="pt-3">
              <DataTable
                columns={[
                  { label: t("Name"), width: "25%" },
                  { label: t("Username"), width: "25%" },
                  { label: t("Role"), width: "25%" },
                  { label: "" },
                ]}
                loading={filteredAdmins === undefined}
                footer={{
                  tableLimit: TABLE_LIMIT,
                  tableOffset: filterState.offset,
                  handleTableOffset: direction => handleTableNavigation(direction),
                  disableNextOffset: !adminData || !(adminData?.length === TABLE_LIMIT),
                }}
                overflow
              >
                {filteredAdmins?.map(admin => {
                  const adminRoles = admin?.roles?.filter((_, index) => index < 3);
                  return (
                    <tr
                      key={admin.id}
                      className={classNames({
                        clickable: authStore?.user?.permissions?.admin_permissions_edit,
                      })}
                      onClick={() => authStore?.user?.permissions?.admin_accounts_edit && history.push("/admin/settings/staff/" + String(admin.id))}
                    >
                      <td>
                        {admin.full_name}{" "}
                        {admin?.state === "disabled" ? <Badge type="warning">{capitalize(admin?.state)}</Badge> : null}
                      </td>
                      <td>{admin.username}</td>
                      <td>{adminRoles?.map(role => role?.title)?.join(", ")} {admin?.roles?.length > 3 ?
                      <OverlayTrigger
                      placement="auto"
                      overlay={<Tooltip id="staff-roles">{admin?.roles?.slice(3)?.map(role => role?.title)?.join(", ")}</Tooltip>}
                    ><span><Badge type="gray" size="small">{t("More")}</Badge></span></OverlayTrigger>
                      : null}</td>
                      <td align="right">
                        <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={
                            <div>
                              <ButtonNew style={{ padding: 10 }} size="medium" type="text">
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </ButtonNew>
                            </div>
                          }
                          sections={[
                            [
                              {
                                label: t("Edit Staff"),
                                icon: "pen",
                                onClick: () => history.push("/admin/settings/staff/" + String(admin.id)),
                                disabled: !authStore?.user?.permissions?.admin_accounts_edit,
                              },
                            ],
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>
          </Card>
        </>
      ) : null}

      {selected === tabSelection.ROLES ? (
        <Card
          title={
            <div className="flex items-center gap-2">
              {t("Roles")}{" "}
              <Badge type={"public"} size="medium">
                {roleState?.length}
              </Badge>
            </div>
          }
          titleActions={[
            {
              content: t("+ New Role"),
              action: () => sendToNewRole(),
              buttonType: "primary",
            },
          ]}
        >
          <div className="pt-3">
            <DataTable
              columns={[{ label: t("Title"), width: "40%" }, { label: t("Staff Accounts") }, { label: "" }]}
              overflow
            // loading={filteredRoles === undefined}
            >
              {roleState?.map(role => (
                <tr
                  key={role.id}
                  className={classNames({
                    clickable: authStore?.user?.permissions?.admin_permissions_edit,
                  })}
                  onClick={() => authStore?.user?.permissions?.admin_permissions_edit && history.push("/admin/settings/roles_edit/" + String(role.id))}
                >
                  <td>
                    {role.title} {role.locked ? <FontAwesomeIcon icon={"lock"} size="sm" /> : null}{" "}
                  </td>
                  <td>{getAdminCountForRole(role.id)}</td>
                  <td align="right">
                    <NavigationDropdownNew
                      showPlainTextLabel
                      rightAlign
                      label={
                        <div>
                          <ButtonNew style={{ padding: 15 }} size="medium" type="text">
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </ButtonNew>
                        </div>
                      }
                      sections={[
                        [
                          {
                            label: t("Edit Role"),
                            icon: "pen",
                            onClick: () => history.push("/admin/settings/roles_edit/" + String(role.id)),
                            disabled: !authStore?.user?.permissions?.admin_permissions_edit,
                          },
                          // {
                          //   label: t("Duplicate Role"),
                          //   icon: "key",
                          //   onClick: () => onResetPassword(),
                          // },
                          {
                            label: t("Delete Role"),
                            icon: "trash-can",
                            onClick: () => updateDeleteRolePopup({ isOpen: true, selectedID: role.id }),
                            disabled: !authStore?.user?.permissions?.admin_permissions_edit,
                            hidden: role?.locked,
                          },
                        ],
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </DataTable>
          </div>
        </Card>
      ) : null}

      {selected === tabSelection.SHIFTS ? (
        <>
          <StaffAccountShiftTable allowCreate allowDelete staffMembers={shiftStaffMembers} />
        </>
      ) : null}
      <Portal isMounted={deleteRolePopup.isOpen}>
        <div className="pricing-sheets-products-modal">
          <Popup
            closable
            title={t("Delete Role?")}
            description={t("Are you sure you want to delete this role? This is permanent.")}
            open={deleteRolePopup.isOpen}
            onOk={deleteRole}
            onCancel={() => updateDeleteRolePopup({ isOpen: false, selectedID: 0 })}
            type={"warning"}
          />
        </div>
      </Portal>
    </Page>
  );
}
